<template>
    <div id="room">
        <header class="room-header">
            <div class="header-left">
                <c-text fontSize="2xl" fontWeight="bold" class="room-title">
                    <i class="fa fa-hashtag"></i> {{$route.params.room}}
                </c-text>
                <button class="icon-btn" @click="copyRoomLink" title="Copy room link">
                    <i class="fa fa-link"></i>
                </button>
            </div>
            <div class="header-center">
                <!-- Voting Progress -->
                <div class="vote-progress" v-if="onTable && !showStats">
                    <div class="progress-ring">
                        <svg viewBox="0 0 36 36" class="progress-svg">
                            <path class="progress-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"/>
                            <path class="progress-fill" :stroke-dasharray="`${voteProgress}, 100`" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"/>
                        </svg>
                        <span class="progress-text">{{ votedCount }}/{{ totalPlayers }}</span>
                    </div>
                    <span class="progress-label">voted</span>
                </div>
                <!-- Timer -->
                <div class="timer-widget" v-if="onTable">
                    <button class="icon-btn icon-btn--sm" @click="toggleTimer">
                        <i :class="timerRunning ? 'fa fa-pause' : 'fa fa-play'"></i>
                    </button>
                    <span class="timer-display">{{ timerDisplay }}</span>
                    <button class="icon-btn icon-btn--sm" @click="resetTimer">
                        <i class="fa fa-refresh"></i>
                    </button>
                </div>
            </div>
            <div class="header-right">
                <c-input 
                    @change="changeName" 
                    @blur="changeName"
                    type="text" 
                    placeholder="Your name..." 
                    class="name-input"
                    width="120px"
                    size="sm"
                />
                <button class="theme-toggle-btn" @click="cycleTheme" :title="'Theme: ' + currentTheme">
                    <i :class="themeIcon"></i>
                </button>
                <label class="auto-reveal-toggle" title="Auto-reveal when everyone votes">
                    <input type="checkbox" v-model="autoReveal" />
                    <span class="toggle-label">Auto</span>
                </label>
                <room-settings></room-settings>
            </div>
        </header>

        <div class="main-content">
            <!-- Left Results Panel (visible after flip) -->
            <aside v-if="showStats" class="results-sidebar">
                <div class="results-header">
                    <span class="results-title">Results</span>
                    <button class="icon-btn icon-btn--sm" @click="$socket.emit('clearTable')" title="New round">
                        <i class="fa fa-refresh"></i>
                    </button>
                </div>
                <div class="results-cards">
                    <div class="result-pill result-pill--primary">
                        <span class="result-pill__value">{{ averageDisplay }}</span>
                        <span class="result-pill__label">AVG</span>
                    </div>
                    <div class="result-pill" v-if="consensus">
                        <span class="result-pill__value">🎉 {{ consensusValue }}</span>
                        <span class="result-pill__label">CONSENSUS</span>
                    </div>
                    <div class="result-pill" v-if="!consensus && closestFib">
                        <span class="result-pill__value">{{ closestFib }}</span>
                        <span class="result-pill__label">SUGGEST</span>
                    </div>
                    <div class="result-pill">
                        <span class="result-pill__value">{{ totalVotes }}</span>
                        <span class="result-pill__label">VOTES</span>
                    </div>
                </div>
                <!-- Vote distribution -->
                <div class="dist-list">
                    <div v-for="entry in voteDistribution" :key="entry.card" class="dist-item">
                        <span class="dist-card">{{ entry.card }}</span>
                        <div class="dist-bar-wrap">
                            <div class="dist-bar" :style="{ width: entry.percent + '%' }"></div>
                        </div>
                        <span class="dist-info">{{ entry.count }}<small> ({{ entry.percent }}%)</small></span>
                    </div>
                </div>
                <div class="dist-voters-section" v-if="voteDistribution.length > 0">
                    <div v-for="entry in voteDistribution" :key="'v'+entry.card" class="voter-row">
                        <span class="voter-card">{{ entry.card }}</span>
                        <span class="voter-names">{{ entry.voters.join(', ') }}</span>
                    </div>
                </div>
            </aside>

            <!-- Table + Sidebar -->
            <div class="table-area">
                <div class="poker-table">
                    <div :class="['table-surface', { 'table-surface--waiting': isWaitingForVotes }]">
                        <div v-if="membersOnTable.length === 0" class="empty-table">
                            <div class="empty-icon">
                                <i class="fa fa-hand-pointer-o"></i>
                            </div>
                            <span>Pick a card to start voting</span>
                        </div>
                        <template v-else>
                            <div class="cards-on-table">
                                <TableCard v-for="member in membersOnTable"
                                    v-bind:key="member.id"
                                    v-bind:member="member">
                                </TableCard>
                            </div>
                            <button v-if="hasHiddenCards" class="reveal-btn" @click="$socket.emit('flipAll')">
                                <i class="fa fa-eye"></i> Reveal cards
                            </button>
                        </template>
                    </div>
                </div>
                <div class="sidebar">
                    <div class="members-list">
                        <div class="members-header">
                            <i class="fa fa-users"></i> Players
                            <span class="player-count">{{ namedMembers.length }}</span>
                        </div>
                        <div v-if="watchers.length > 0" class="watchers-count">
                            <i class="fa fa-eye"></i> {{ watchers.length }} spectating
                        </div>
                        <ul class="member-items">
                            <li v-for="member in namedMembers" :key="member.id" class="member-item">
                                <div class="member-info">
                                    <span :class="['member-name', { 'is-me': member.id === (me && me.id) }]">
                                        {{ member.name }}
                                    </span>
                                    <span v-if="playerReactions[member.id]" class="member-reaction">{{ playerReactions[member.id] }}</span>
                                </div>
                                <span v-if="member.card != null && member.hidden" class="status-badge status-badge--voted">✓</span>
                                <span v-else-if="member.card != null && !member.hidden" class="status-badge status-badge--revealed">
                                    {{ $store.getters.deck[member.card] }}
                                </span>
                                <span v-else class="status-badge status-badge--waiting">...</span>
                            </li>
                        </ul>
                    </div>
                    <!-- Emoji reactions -->
                    <div class="emoji-bar">
                        <button v-for="emoji in emojis" :key="emoji" class="emoji-btn" @click="sendReaction(emoji)" :title="'React ' + emoji">
                            {{ emoji }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <Hand v-if="onTable" :autoReveal="autoReveal"/>

        <!-- Celebration overlay -->
        <div v-if="showCelebration" class="celebration-overlay">
            <div class="celebration-text">🎉 Consensus! 🎉</div>
            <div class="clap-emojis">
                <span v-for="n in 8" :key="n" class="floating-clap" :style="{ animationDelay: (n * 0.15) + 's', left: (10 + n * 10) + '%' }">👏</span>
            </div>
        </div>

        <!-- Floating reactions -->
        <transition-group name="reaction-float" tag="div" class="floating-reactions">
            <div v-for="r in floatingReactions" :key="r.id" class="floating-reaction" :style="{ left: r.x + '%' }">
                {{ r.emoji }}
            </div>
        </transition-group>

        <!-- Copied toast -->
        <transition name="fade">
            <div v-if="showCopied" class="toast">Link copied!</div>
        </transition>
    </div>
</template>

<script lang='ts'>
import Hand from '../components/Hand.vue'
import RoomSettings from '@/components/RoomSettings.vue'
import TableCard from '../components/TableCard.vue'
import { TargetEvent } from '../types'
import { CText, CInput } from "@chakra-ui/vue"
import Vue from 'vue'

export default Vue.extend({
    name: 'room',
    components: {
        Hand,
        TableCard,
        CInput,
        CText,
        RoomSettings,
    },
    data() {
        return {
            timerSeconds: 0,
            timerRunning: false,
            timerInterval: null as any,
            showCelebration: false,
            showCopied: false,
            lastConsensusState: false,
            autoReveal: false,
            currentTheme: localStorage.getItem('pp-theme') || 'light',
            emojis: ['👍', '👏', '🎉', '🤔', '☕', '🚀'],
            playerReactions: {} as Record<string, string>,
            floatingReactions: [] as Array<{id: number, emoji: string, x: number}>,
            reactionCounter: 0,
        }
    },
    created() {
        const {room} = this.$route.params
        this.$socket.emit('join', room)
    },
    beforeDestroy() {
        if (this.timerInterval) clearInterval(this.timerInterval)
    },
    watch: {
        consensus(newVal) {
            if (newVal && !this.lastConsensusState) {
                this.celebrate()
            }
            this.lastConsensusState = newVal
        },
        allVoted(newVal) {
            if (newVal && this.autoReveal && this.votedCount >= 2) {
                this.$socket.emit('flipAll')
            }
        }
    },
    computed: {
        me() {
            return this.$store.getters.members
                .find(mem => mem.id == this.$socket.id)
        },
        watchers() {
            return this.$store.getters.members 
                .filter(mem => mem.name == '')
        },
        namedMembers() {
            return this.$store.getters.members.filter(mem => mem.name != '')
        },
        membersOnTable() {
            return this.$store.getters.members
                .filter(mem => mem.card != null)
        },
        hasHiddenCards() {
            return this.membersOnTable.some(m => m.hidden)
        },
        isWaitingForVotes() {
            // Pulse when some have voted but not all
            return this.votedCount > 0 && this.votedCount < this.totalPlayers && this.hasHiddenCards
        },
        onTable() {
            if(this.me == null) return false
            return this.me.name != ''
        },
        votedCount() {
            return this.namedMembers.filter(m => m.card != null).length
        },
        totalPlayers() {
            return this.namedMembers.length
        },
        allVoted() {
            return this.totalPlayers > 0 && this.votedCount === this.totalPlayers
        },
        voteProgress() {
            if (this.totalPlayers === 0) return 0
            return Math.round((this.votedCount / this.totalPlayers) * 100)
        },
        showStats() {
            const room = this.$store.getters.room
            return room && room.eventName === 'flipAll' && room.totalCard > 0
        },
        averageDisplay() {
            const room = this.$store.getters.room
            if (!room || !room.averageCard) return '-'
            return Math.round(room.averageCard * 10) / 10
        },
        totalVotes() {
            const room = this.$store.getters.room
            return room?.totalCard || 0
        },
        consensus() {
            const room = this.$store.getters.room
            if (!room || room.eventName !== 'flipAll') return false
            const members = this.$store.getters.members.filter(m => m.card != null && !m.hidden)
            if (members.length < 2) return false
            const firstCard = members[0].card
            return members.every(m => m.card === firstCard)
        },
        consensusValue() {
            const members = this.$store.getters.members.filter(m => m.card != null && !m.hidden)
            if (members.length === 0) return '-'
            return this.$store.getters.deck[members[0].card]
        },
        closestFib() {
            const room = this.$store.getters.room
            if (!room || !room.averageCard) return null
            const deck = this.$store.getters.deck
            if (!deck || deck.length === 0) return null
            const numDeck = deck.filter(c => typeof c === 'number')
            if (numDeck.length === 0) return null
            const avg = room.averageCard
            let closest = numDeck[0]
            let minDiff = Math.abs(numDeck[0] - avg)
            for (const card of numDeck) {
                const diff = Math.abs(card - avg)
                if (diff < minDiff) {
                    minDiff = diff
                    closest = card
                }
            }
            return closest
        },
        voteDistribution() {
            const room = this.$store.getters.room
            if (!room || !room.cardCounts) return []
            const deck = this.$store.getters.deck
            const members = this.$store.getters.members.filter(m => m.card != null && !m.hidden)
            const total = members.length || 1
            
            const distribution: any[] = []
            const counts = room.cardCounts
            for (const [cardVal, count] of Object.entries(counts)) {
                const voters = members
                    .filter(m => deck[m.card] == cardVal)
                    .map(m => m.name)
                distribution.push({
                    card: cardVal,
                    count: count as number,
                    percent: Math.round(((count as number) / total) * 100),
                    voters
                })
            }
            return distribution.sort((a, b) => b.count - a.count)
        },
        mostVoted() {
            const room = this.$store.getters.room
            if (!room || !room.cardCounts) return null
            const counts = room.cardCounts
            const entries = Object.entries(counts)
            if (entries.length === 0) return null
            const max = entries.reduce((a, b) => (b[1] as number) > (a[1] as number) ? b : a)
            return max[0]
        },
        timerDisplay() {
            const mins = Math.floor(this.timerSeconds / 60)
            const secs = this.timerSeconds % 60
            return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
        },
        themeIcon() {
            if (this.currentTheme === 'dark') return 'fa fa-moon-o'
            if (this.currentTheme === 'light') return 'fa fa-sun-o'
            return 'fa fa-magic'
        }
    },
    methods: {
        changeName(event:TargetEvent) {
            const name = event.target.value
            this.$socket.emit('changeName', name)
        },
        cycleTheme() {
            const themes = ['light', 'dark', 'auto']
            const idx = themes.indexOf(this.currentTheme)
            this.currentTheme = themes[(idx + 1) % themes.length]
            localStorage.setItem('pp-theme', this.currentTheme)
            let effective = this.currentTheme
            if (effective === 'auto') {
                effective = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
            }
            document.documentElement.setAttribute('data-theme', effective)
        },
        sendReaction(emoji: string) {
            // Show reaction on self
            if (this.me) {
                this.$set(this.playerReactions, this.me.id, emoji)
                setTimeout(() => { this.$delete(this.playerReactions, this.me.id) }, 2500)
            }
            // Float reaction up
            this.reactionCounter++
            const reaction = { id: this.reactionCounter, emoji, x: 20 + Math.random() * 60 }
            this.floatingReactions.push(reaction)
            setTimeout(() => {
                const idx = this.floatingReactions.findIndex(r => r.id === reaction.id)
                if (idx !== -1) this.floatingReactions.splice(idx, 1)
            }, 2000)
        },
        copyRoomLink() {
            const url = window.location.href
            navigator.clipboard.writeText(url).then(() => {
                this.showCopied = true
                setTimeout(() => { this.showCopied = false }, 2000)
            })
        },
        toggleTimer() {
            if (this.timerRunning) {
                clearInterval(this.timerInterval)
                this.timerRunning = false
            } else {
                this.timerInterval = setInterval(() => {
                    this.timerSeconds++
                }, 1000)
                this.timerRunning = true
            }
        },
        resetTimer() {
            clearInterval(this.timerInterval)
            this.timerRunning = false
            this.timerSeconds = 0
        },
        celebrate() {
            this.showCelebration = true
            this.playCelebrationSound()
            this.fireConfetti()
            // Show clap reaction on all players
            const members = this.$store.getters.members || []
            members.forEach((m: any) => {
                this.$set(this.playerReactions, m.id, '👏')
            })
            setTimeout(() => {
                this.showCelebration = false
                this.playerReactions = {}
            }, 3000)
        },
        playCelebrationSound() {
            const soundEnabled = localStorage.getItem('pp-sound') !== 'false'
            if (!soundEnabled) return
            const soundType = localStorage.getItem('pp-sound-type') || 'chime'
            try {
                const ctx = new (window.AudioContext || (window as any).webkitAudioContext)()
                if (soundType === 'chime') {
                    const notes = [523.25, 659.25, 783.99, 1046.50]
                    notes.forEach((freq, i) => {
                        const osc = ctx.createOscillator()
                        const gain = ctx.createGain()
                        osc.connect(gain); gain.connect(ctx.destination)
                        osc.type = 'sine'; osc.frequency.value = freq
                        gain.gain.setValueAtTime(0.25, ctx.currentTime + i * 0.12)
                        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.12 + 0.35)
                        osc.start(ctx.currentTime + i * 0.12)
                        osc.stop(ctx.currentTime + i * 0.12 + 0.35)
                    })
                } else if (soundType === 'fanfare') {
                    const notes = [392, 523.25, 659.25, 783.99, 1046.50]
                    notes.forEach((freq, i) => {
                        const osc = ctx.createOscillator()
                        const gain = ctx.createGain()
                        osc.connect(gain); gain.connect(ctx.destination)
                        osc.type = 'triangle'; osc.frequency.value = freq
                        gain.gain.setValueAtTime(0.3, ctx.currentTime + i * 0.1)
                        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.1 + 0.5)
                        osc.start(ctx.currentTime + i * 0.1)
                        osc.stop(ctx.currentTime + i * 0.1 + 0.5)
                    })
                } else if (soundType === 'bell') {
                    const osc = ctx.createOscillator()
                    const gain = ctx.createGain()
                    osc.connect(gain); gain.connect(ctx.destination)
                    osc.type = 'sine'; osc.frequency.value = 830
                    gain.gain.setValueAtTime(0.4, ctx.currentTime)
                    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 1.0)
                    osc.start(ctx.currentTime)
                    osc.stop(ctx.currentTime + 1.0)
                } else if (soundType === 'pop') {
                    const osc = ctx.createOscillator()
                    const gain = ctx.createGain()
                    osc.connect(gain); gain.connect(ctx.destination)
                    osc.type = 'square'
                    osc.frequency.setValueAtTime(600, ctx.currentTime)
                    osc.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.1)
                    gain.gain.setValueAtTime(0.3, ctx.currentTime)
                    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15)
                    osc.start(ctx.currentTime)
                    osc.stop(ctx.currentTime + 0.15)
                } else if (soundType === 'clap') {
                    // Simulate clapping with noise bursts
                    for (let i = 0; i < 5; i++) {
                        const bufSize = 4096
                        const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate)
                        const data = buf.getChannelData(0)
                        for (let j = 0; j < bufSize; j++) { data[j] = Math.random() * 2 - 1 }
                        const noise = ctx.createBufferSource()
                        noise.buffer = buf
                        const bpf = ctx.createBiquadFilter()
                        bpf.type = 'bandpass'
                        bpf.frequency.value = 1200 + i * 200
                        bpf.Q.value = 1.5
                        const gain = ctx.createGain()
                        noise.connect(bpf); bpf.connect(gain); gain.connect(ctx.destination)
                        const t = ctx.currentTime + i * 0.12
                        gain.gain.setValueAtTime(0.35, t)
                        gain.gain.exponentialRampToValueAtTime(0.01, t + 0.08)
                        noise.start(t)
                        noise.stop(t + 0.08)
                    }
                }
            } catch (e) { /* noop */ }
        },
        async fireConfetti() {
            try {
                const confetti = (await import('canvas-confetti')).default
                confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } })
                setTimeout(() => {
                    confetti({ particleCount: 50, angle: 60, spread: 55, origin: { x: 0 } })
                    confetti({ particleCount: 50, angle: 120, spread: 55, origin: { x: 1 } })
                }, 300)
            } catch (e) { /* noop */ }
        }
    }
})
</script>

<style scoped>
#room {
    height: 100vh;
    width: 100%;
    display: flex;
    flex-direction: column;
    background: var(--bg-primary);
    color: var(--text-primary);
    overflow: hidden;
}

/* Theme toggle */
.theme-toggle-btn {
    background: var(--card-bg);
    border: 1px solid var(--border-color);
    color: var(--text-secondary);
    border-radius: 8px;
    padding: 5px 9px;
    cursor: pointer;
    transition: all 0.2s;
    font-size: 0.82em;
    box-shadow: var(--shadow-sm);
}

.theme-toggle-btn:hover {
    background: var(--card-hover);
    color: var(--accent);
    box-shadow: var(--shadow-md);
}

/* Header */
.room-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 20px;
    background: var(--header-bg);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--border-color);
    flex-shrink: 0;
    box-shadow: var(--shadow-sm);
    z-index: 10;
}

.header-left, .header-right, .header-center {
    display: flex;
    align-items: center;
    gap: 8px;
}

.room-title {
    color: var(--text-primary) !important;
    font-size: 1.1em !important;
}

.icon-btn {
    background: var(--card-bg);
    border: 1px solid var(--border-color);
    color: var(--text-secondary);
    border-radius: 8px;
    padding: 5px 9px;
    cursor: pointer;
    transition: all 0.2s;
    font-size: 0.82em;
    box-shadow: var(--shadow-sm);
}

.icon-btn:hover {
    background: var(--card-hover);
    color: var(--text-primary);
    box-shadow: var(--shadow-md);
}

.icon-btn--sm {
    padding: 3px 6px;
    font-size: 0.72em;
}

.auto-reveal-toggle {
    display: flex;
    align-items: center;
    gap: 3px;
    cursor: pointer;
    font-size: 0.72em;
    color: var(--text-tertiary);
}

.auto-reveal-toggle input {
    accent-color: var(--accent);
    width: 13px;
    height: 13px;
}

.toggle-label { white-space: nowrap; }

/* Vote progress */
.vote-progress {
    display: flex;
    align-items: center;
    gap: 5px;
}

.progress-ring {
    position: relative;
    width: 28px;
    height: 28px;
}

.progress-svg {
    width: 100%;
    height: 100%;
    transform: rotate(-90deg);
}

.progress-bg {
    fill: none;
    stroke: var(--border-color);
    stroke-width: 3;
}

.progress-fill {
    fill: none;
    stroke: var(--accent);
    stroke-width: 3;
    stroke-linecap: round;
    transition: stroke-dasharray 0.4s ease;
}

.progress-text {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 0.5em;
    font-weight: bold;
    color: var(--text-primary);
}

.progress-label {
    font-size: 0.68em;
    color: var(--text-tertiary);
}

/* Timer */
.timer-widget {
    display: flex;
    align-items: center;
    gap: 5px;
    background: var(--card-bg);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    padding: 4px 8px;
    box-shadow: var(--shadow-sm);
}

.timer-display {
    font-family: 'JetBrains Mono', 'Courier New', monospace;
    font-size: 0.8em;
    color: var(--text-secondary);
    min-width: 40px;
    text-align: center;
}

/* Main content - horizontal layout */
.main-content {
    flex: 1;
    display: flex;
    overflow: hidden;
    min-height: 0;
}

/* Results Sidebar (left) */
.results-sidebar {
    width: 240px;
    flex-shrink: 0;
    background: var(--panel-bg);
    border-right: 1px solid var(--border-color);
    padding: 14px;
    overflow-y: auto;
    animation: slideIn 0.3s ease;
    box-shadow: var(--shadow-md);
}

@keyframes slideIn {
    from { opacity: 0; transform: translateX(-20px); }
    to { opacity: 1; transform: translateX(0); }
}

.results-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.results-title {
    font-weight: 700;
    font-size: 0.85em;
    color: var(--text-primary);
}

.results-cards {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-bottom: 14px;
}

.result-pill {
    background: var(--card-bg);
    border: 1px solid var(--border-color);
    border-radius: 10px;
    padding: 10px 8px;
    text-align: center;
    box-shadow: var(--shadow-sm);
}

.result-pill--primary {
    background: var(--success-subtle);
    border-color: var(--success);
}

.result-pill__value {
    display: block;
    font-size: 1.3em;
    font-weight: 800;
    color: var(--text-primary);
    line-height: 1.2;
}

.result-pill__label {
    display: block;
    font-size: 0.55em;
    text-transform: uppercase;
    letter-spacing: 1.2px;
    color: var(--text-tertiary);
    margin-top: 2px;
    font-weight: 700;
}

/* Distribution */
.dist-list {
    margin-bottom: 10px;
}

.dist-item {
    display: grid;
    grid-template-columns: 32px 1fr 60px;
    align-items: center;
    gap: 6px;
    padding: 3px 0;
}

.dist-card {
    background: var(--card-bg);
    border: 1px solid var(--border-color);
    border-radius: 5px;
    padding: 2px 6px;
    text-align: center;
    font-weight: 700;
    font-size: 0.78em;
    color: var(--text-primary);
}

.dist-bar-wrap {
    height: 5px;
    background: var(--card-bg);
    border-radius: 3px;
    overflow: hidden;
}

.dist-bar {
    height: 100%;
    background: linear-gradient(90deg, var(--accent), var(--success));
    border-radius: 3px;
    transition: width 0.5s ease;
}

.dist-info {
    font-size: 0.72em;
    color: var(--text-secondary);
    text-align: right;
}

.dist-info small { opacity: 0.6; }

.dist-voters-section {
    border-top: 1px solid var(--border-color);
    padding-top: 8px;
}

.voter-row {
    display: flex;
    gap: 6px;
    align-items: center;
    padding: 2px 0;
}

.voter-card {
    font-weight: 700;
    font-size: 0.72em;
    color: var(--accent);
    min-width: 20px;
}

.voter-names {
    font-size: 0.7em;
    color: var(--text-tertiary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Table Area */
.table-area {
    flex: 1;
    display: flex;
    gap: 12px;
    padding: 12px 16px;
    overflow: hidden;
    min-height: 0;
}

.poker-table {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.table-surface {
    background: var(--table-gradient);
    border: 1.5px solid var(--table-border);
    border-radius: 20px;
    padding: 28px 36px;
    min-height: 180px;
    width: 100%;
    max-width: 700px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 20px;
    box-shadow: var(--shadow-lg), inset 0 1px 12px var(--table-glow);
    transition: box-shadow 0.3s ease;
}

.table-surface--waiting {
    animation: tablePulse 2s ease-in-out infinite;
}

@keyframes tablePulse {
    0%, 100% { box-shadow: var(--shadow-lg), inset 0 1px 12px var(--table-glow); }
    50% { box-shadow: var(--shadow-lg), inset 0 1px 12px var(--table-glow), 0 0 20px var(--accent-glow); }
}

.empty-table {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    color: var(--text-tertiary);
    font-size: 0.88em;
}

.empty-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: var(--accent-subtle);
    display: flex;
    align-items: center;
    justify-content: center;
    animation: float 3s ease-in-out infinite;
}

.empty-icon i {
    font-size: 1.3em;
    color: var(--accent);
}

@keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-6px); }
}

.cards-on-table {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 16px;
}

.reveal-btn {
    padding: 10px 24px;
    background: var(--accent);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 0.85em;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 4px 14px var(--accent-glow);
}

.reveal-btn:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 6px 20px var(--accent-glow);
}

.reveal-btn:active {
    transform: scale(0.97);
}

/* Sidebar */
.sidebar {
    width: 170px;
    flex-shrink: 0;
    background: var(--panel-bg);
    border: 1px solid var(--border-color);
    border-radius: 14px;
    padding: 12px;
    overflow-y: auto;
    box-shadow: var(--shadow-card);
}

.members-header {
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 10px;
    font-size: 0.8em;
    display: flex;
    align-items: center;
    gap: 5px;
}

.player-count {
    background: var(--accent-subtle);
    color: var(--accent);
    border-radius: 8px;
    padding: 1px 6px;
    font-size: 0.78em;
    font-weight: 700;
}

.watchers-count {
    color: var(--text-tertiary);
    font-size: 0.68em;
    margin-bottom: 6px;
}

.member-items {
    list-style: none;
    padding: 0;
    margin: 0;
}

.member-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 6px;
    border-radius: 6px;
    margin-bottom: 2px;
    transition: background 0.2s;
}

.member-item:hover {
    background: var(--card-hover);
}

.member-name {
    color: var(--text-secondary);
    font-size: 0.76em;
}

.member-name.is-me {
    font-weight: bold;
    color: var(--text-primary);
}

.status-badge {
    font-size: 0.65em;
    padding: 2px 6px;
    border-radius: 5px;
    font-weight: 700;
}

.status-badge--voted {
    background: var(--success-subtle);
    color: var(--success);
}

.status-badge--revealed {
    background: var(--accent-subtle);
    color: var(--accent);
}

.status-badge--waiting {
    color: var(--text-tertiary);
}

/* Member info with reaction */
.member-info {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    flex: 1;
}

.member-reaction {
    font-size: 0.9em;
    animation: reactionPop 0.3s ease;
}

@keyframes reactionPop {
    0% { transform: scale(0); }
    60% { transform: scale(1.4); }
    100% { transform: scale(1); }
}

/* Emoji bar */
.emoji-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    padding-top: 10px;
    margin-top: 10px;
    border-top: 1px solid var(--border-color);
    justify-content: center;
}

.emoji-btn {
    background: var(--card-bg);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    padding: 4px 7px;
    font-size: 1em;
    cursor: pointer;
    transition: all 0.2s;
    line-height: 1;
}

.emoji-btn:hover {
    transform: scale(1.25);
    background: var(--card-hover);
    box-shadow: var(--shadow-md);
}

/* Floating reactions */
.floating-reactions {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 999;
}

.floating-reaction {
    position: absolute;
    bottom: 100px;
    font-size: 2em;
    animation: floatUp 2s ease forwards;
}

@keyframes floatUp {
    0% { opacity: 1; transform: translateY(0) scale(1); }
    100% { opacity: 0; transform: translateY(-200px) scale(1.5); }
}

.reaction-float-enter-active { transition: all 0.3s; }
.reaction-float-leave-active { transition: all 0.5s; }
.reaction-float-enter { opacity: 0; transform: scale(0); }
.reaction-float-leave-to { opacity: 0; }

/* Clap emojis in celebration */
.clap-emojis {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    pointer-events: none;
}

.floating-clap {
    position: absolute;
    bottom: 20%;
    font-size: 2em;
    animation: clapFloat 2.5s ease forwards;
}

@keyframes clapFloat {
    0% { opacity: 0; transform: translateY(0) scale(0.5); }
    20% { opacity: 1; transform: translateY(-30px) scale(1.2); }
    100% { opacity: 0; transform: translateY(-180px) scale(0.8) rotate(20deg); }
}

/* Celebration */
.celebration-overlay {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    z-index: 1000;
    animation: celebFade 3s ease forwards;
}

@keyframes celebFade {
    0% { opacity: 0; transform: scale(0.5); }
    20% { opacity: 1; transform: scale(1.1); }
    30% { transform: scale(1); }
    80% { opacity: 1; }
    100% { opacity: 0; }
}

.celebration-text {
    font-size: 2.5em;
    font-weight: bold;
    color: var(--text-primary);
    text-shadow: 0 4px 20px var(--accent-glow);
}

/* Toast */
.toast {
    position: fixed;
    bottom: 80px;
    left: 50%;
    transform: translateX(-50%);
    background: var(--panel-bg);
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    padding: 8px 20px;
    border-radius: 20px;
    font-size: 0.78em;
    z-index: 1001;
    box-shadow: var(--shadow-lg);
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter, .fade-leave-to { opacity: 0; }

/* Responsive */
@media (max-width: 768px) {
    .main-content {
        flex-direction: column;
    }
    .results-sidebar {
        width: 100%;
        max-height: 160px;
        border-right: none;
        border-bottom: 1px solid var(--border-color);
    }
    .results-cards {
        grid-template-columns: repeat(4, 1fr);
    }
    .table-area {
        flex-direction: column;
    }
    .sidebar {
        width: 100%;
        max-height: 100px;
    }
    .header-center {
        display: none;
    }
}
</style>